/**
 * Macro Recording Take — persistence unit.
 *
 * Redis JSON document, full-replace + rev CAS.
 * Keys: `realtime:campaign:macro-recording:{campaignId}:{takeId}`
 * Index Set: `realtime:campaign:macro-recordings:{campaignId}`
 */

import { z } from 'zod';
import { macroRegionSchema } from './macroTree';

// ─── Input payload (discriminated by device) ────────────────────────────────

/** Intermediate drag sample: delta since previous waypoint, ms since gesture start. */
export const macroRecordingDragWaypointSchema = z.object({
  dx: z.number(),
  dy: z.number(),
  atMs: z.number(),
});

const dragPayloadFields = {
  startX: z.number(),
  startY: z.number(),
  endX: z.number(),
  endY: z.number(),
  totalDx: z.number(),
  totalDy: z.number(),
  durationMs: z.number(),
  waypoints: z.array(macroRecordingDragWaypointSchema),
} as const;

// NOTE: z.union, not z.discriminatedUnion — zod rejects duplicate discriminator
// values, and 'mouse'/'touch' each appear twice (standard actions + drag).
export const macroRecordingInputPayloadSchema = z.union([
  z.object({
    device: z.literal('keyboard'),
    keyCode: z.number().int(),
    location: z.number().int(),
    modifiers: z.object({
      shift: z.boolean(),
      ctrl: z.boolean(),
      alt: z.boolean(),
    }),
  }),
  z.object({ device: z.literal('gamepad'), button: z.string() }),
  z
    .object({
      device: z.literal('mouse'),
      action: z.enum(['down', 'up']),
      button: z.enum(['left', 'right', 'middle']).optional(),
      dx: z.number().optional(),
      dy: z.number().optional(),
      x: z.number().min(0).max(1).optional(),
      y: z.number().min(0).max(1).optional(),
    })
    .superRefine((val, ctx) => {
      const hasX = val.x !== undefined;
      const hasY = val.y !== undefined;
      if (hasX !== hasY) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'x and y must both be present or both omitted',
        });
      }
    }),
  z.object({
    device: z.literal('mouse'),
    action: z.enum(['move', 'wheel']),
    button: z.enum(['left', 'right', 'middle']).optional(),
    dx: z.number().optional(),
    dy: z.number().optional(),
  }),
  z.object({
    device: z.literal('mouse'),
    action: z.literal('drag'),
    ...dragPayloadFields,
  }),
  z.object({
    device: z.literal('touch'),
    action: z.enum(['down', 'move', 'up']),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
  }),
  z.object({
    device: z.literal('touch'),
    action: z.literal('drag'),
    startX: z.number().min(0).max(1),
    startY: z.number().min(0).max(1),
    endX: z.number().min(0).max(1),
    endY: z.number().min(0).max(1),
    totalDx: z.number(),
    totalDy: z.number(),
    durationMs: z.number(),
    waypoints: z.array(macroRecordingDragWaypointSchema),
  }),
]);

export type MacroRecordingInputPayload = z.infer<typeof macroRecordingInputPayloadSchema>;

// ─── Take document ──────────────────────────────────────────────────────────

export const macroRecordingTakeSchema = z.object({
  takeId: z.string().uuid(),
  campaignId: z.string().min(1),
  name: z.string().min(1).max(80),
  status: z.enum(['recording', 'finalized', 'published']),
  /**
   * Derived from the campaign's Supported Input Methods (campaignPointerMode.ts);
   * when the campaign supports 'both', the operator chooses before recording.
   * 'neither' prevents pointer recording — the take records keyboard/gamepad only.
   */
  mouseMode: z.enum(['mouse', 'touch']),
  createdAt: z.number(),
  updatedAt: z.number(),
  rev: z.number().int(),
  /**
   * video.key is set by realtime-api on HOST_MACRO_RECORDING_UPLOAD_COMPLETE;
   * durationMs/width/height are backfilled by the editor from the <video>
   * element's loaded metadata (Phase 5) — no native/WS message carries them.
   * startTimestampMs stays optional: alignment uses per-mark native timestampMs
   * + videoTimebaseOffsetMs, not the MP4 wall-clock.
   */
  video: z.object({
    key: z.string(),
    durationMs: z.number(),
    width: z.number(),
    height: z.number(),
    startTimestampMs: z.number().optional(),
  }).nullable(),
  videoTimebaseOffsetMs: z.number().optional(),
  timelineInputs: z.array(z.object({
    id: z.string().uuid(),
    atMs: z.number(),
    kind: z.enum(['tap', 'chord', 'hold', 'drag']),
    durMs: z.number().optional(),
    label: z.string(),
    payload: macroRecordingInputPayloadSchema,
  })),
  timelineScreenStates: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    atMs: z.number(),
    customName: z.boolean().optional(),
    index: z.number().int().optional(),
    timestampMs: z.number().optional(),
    hash: z.string().regex(/^[0-9a-f]{16}$/).optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    imageKey: z.string().optional(),
    previewUrl: z.string().url().optional(),
    region: macroRegionSchema.optional(),
    /** `regionKey` this `hash` was computed for; `""` = full-frame. Absent on pre-region takes. */
    hashedRegionKey: z.string().optional(),
  })),
  publishedAtMs: z.number().optional(),
});

export type MacroRecordingTake = z.infer<typeof macroRecordingTakeSchema>;
